/**
 * Mass Messages → Auto: de automatische Mass Messages nalopen en goedkeuren.
 *
 * 11-10-2026 (Jordy): "Per model 3 tekst-MM per dag, een week vooruit, alles
 * als concept → jullie keuren na/goed → goedgekeurde gaan automatisch op hun
 * tijd uit."
 *
 * Boven: de schakelaars per model (genereren / verzenden / nachtslots) en de
 * caps. Iedereen met de pagina ziet ze; alleen een owner kan ze wijzigen (de
 * server weigert de rest ook).
 *
 * Daaronder de concepten per model per dag, elke tekst volledig. Een concept
 * telt als "gezien" zodra het echt in beeld is geweest (IntersectionObserver);
 * "Approve these N" keurt alleen gezien concepten goed, elk met de hash van
 * wat in beeld stond (lib/mmAuto.ts). Per tekst: Edit, Reject, Regenerate.
 * Filters (model, platform, status, week) en bladeren.
 *
 * Er wordt hier niets verstuurd: goedgekeurd gaat pas op zijn tijd uit, en
 * alleen als de schakelaar "verzenden" van het model aan staat.
 *
 * 16-10-2026 (Jordy, [mm]): "Start" — de batch naar een nieuw startmoment
 * schuiven (owner), en Approve all / Send approved voor ALLES of alleen deze
 * lijst, elk met de telling in de knop en één bevestiging.
 */
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Empty, ErrorNote, Loading, Panel, Plat, Seg, Tag } from '../components/ui'
import { useApi, usePoll } from '../lib/useApi'
import { useActions } from '../lib/actions'
import { api } from '../lib/api'
import { maandagVan, plusDagen, vandaagAmsterdam, vensterUtc, weekLabel } from '../lib/agenda'
import { useMijnZone } from '../lib/tijdzone'
import { TijdKiezer } from '../components/TijdKiezer'
import { standaardMoment, type DatumTijd } from '../lib/tijdkeuze'
import { alsZone, nlDagLabel, nlDatum, zoneDelen, zoneLabel, zoneNaarUtc } from '../lib/amsterdam'
import {
  type AutoConcept, type Gezien, bulkVerzoek, goedTeKeuren, groepeer, isGezien, keuzesVoorConcept,
  lokaleTijd, markeerGezien, NIVEAU_NAAM, typeLabel, WEEKDAG_NAAM,
} from '../lib/mmAuto'
import { modelKeuzes } from '../components/MassDmFormulier'
import { magBetaaldeMassDm, magMassDm, useMij } from '../lib/rechten'
import { ConceptBewerken, type Concept } from './MassaConcepten'
import type { Persona } from './Models'

const AUTO = '/api/admin/mass-auto'
const CONCEPTEN = '/api/admin/conversations/mass-message/drafts'
const PER_PAGINA = 200

interface ModelStand {
  slug: string
  name: string
  platform: string
  timezone: string
  generate: boolean
  send: boolean
  night_slots: boolean
}

interface Instellingen {
  models: ModelStand[]
  daily_cap_per_model: number
  hourly_cap_per_brand: number
  generation_weekday: number
  last_generated_week: string | null
  system_account: { exists: boolean; active: boolean }
  drafts_expired_last_7_days: number
  can_edit: boolean
}

interface Lijst {
  items: AutoConcept[]
  total: number
  next_offset: number | null
  can_approve: boolean
}

const STATUSSEN = [
  { value: 'draft', label: 'To review' },
  { value: 'approved', label: 'Approved' },
  { value: 'draft_rejected', label: 'Rejected' },
  { value: 'draft_expired', label: 'Expired' },
]

export function MassaAuto() {
  const zone = useMijnZone()
  const { run, busy } = useActions()
  const inst = useApi<Instellingen>(`${AUTO}/settings`)
  const [model, setModel] = useState('')
  const [platform, setPlatform] = useState('')
  const [status, setStatus] = useState('draft')
  const [maandag, setMaandag] = useState(() => maandagVan(plusDagen(vandaagAmsterdam(undefined, zone), 7)))
  const [offset, setOffset] = useState(0)
  const [gezien, setGezien] = useState<Gezien>(() => new Map())

  const venster = vensterUtc(maandag, plusDagen(maandag, 6), zone)
  const params = new URLSearchParams({
    origin: 'auto', order: 'time', status, from: venster.from, to: venster.to,
    offset: String(offset), limit: String(PER_PAGINA),
  })
  if (model) params.set('model_slug', model)
  if (platform) params.set('platform', platform)
  const q = useApi<Lijst>(`${CONCEPTEN}?${params.toString()}`)
  const groepen = useMemo(() => groepeer(q.data?.items ?? []), [q.data])
  useEffect(() => { setOffset(0) }, [model, platform, status, maandag])

  const opnieuw = () => { q.refetch(); inst.refetch() }
  const kanWijzigen = Boolean(inst.data?.can_edit)
  // 19-10-2026 (Jordy): na Generate de week van het startmoment tonen, in "To review",
  // zodat de nieuwe concepten meteen zichtbaar zijn (de lijst stond standaard op volgende week).
  const toonWeek = useCallback((iso: string | null) => {
    const datum = zoneDelen(iso, zone)?.datum
    if (datum) setMaandag(maandagVan(datum))
    setStatus('draft')
  }, [zone])
  // Lege lijst: waar staan de concepten die nog beoordeeld moeten worden? (eerstvolgende vanaf nu)
  const leeg = Boolean(q.data && !q.data.items.length)
  const eldersVanaf = useMemo(() => new Date().toISOString().slice(0, 16) + ':00Z', [leeg, model, platform, maandag])
  const elders = new URLSearchParams({ origin: 'auto', order: 'time', status: 'draft', from: eldersVanaf, limit: '1' })
  if (model) elders.set('model_slug', model)
  if (platform) elders.set('platform', platform)
  const eldersQ = useApi<Lijst>(leeg && status === 'draft' ? `${CONCEPTEN}?${elders.toString()}` : null)
  const eersteElders = eldersQ.data?.items[0]?.scheduled_at ?? null
  const eldersMaandag = eersteElders ? maandagVan(zoneDelen(eersteElders, zone)?.datum ?? maandag) : null

  return (
    <div className="col" style={{ gap: 14 }} data-testid="mm-auto">
      <Schakelaars inst={inst.data} laden={inst.loading} fout={inst.error} kanWijzigen={kanWijzigen} zone={zone} onKlaar={opnieuw}
                   onToon={toonWeek} />
      {kanWijzigen ? <StartVerschuiven zone={zone} onKlaar={opnieuw} /> : null}
      <Panel title="Automatic drafts" desc={weekLabel(maandag)}>
        <div className="filters" style={{ flexWrap: 'wrap', gap: 8 }}>
          <button className="btn ghost sm" type="button" onClick={() => setMaandag(plusDagen(maandag, -7))}>‹ Prev week</button>
          <button className="btn ghost sm" type="button" onClick={() => setMaandag(plusDagen(maandag, 7))}>Next week ›</button>
          <select value={model} onChange={(e) => setModel(e.target.value)} aria-label="Model" data-testid="mm-auto-model">
            <option value="">All models</option>
            {(inst.data?.models ?? []).map((m) => <option key={m.slug} value={m.slug}>{m.name} ({m.platform === 'f2f' ? 'F2F' : 'FC'})</option>)}
          </select>
          <Seg options={[{ value: '', label: 'All' }, { value: 'fancentro', label: 'FC' }, { value: 'f2f', label: 'F2F' }]}
               value={platform} onChange={setPlatform} />
          <Seg options={STATUSSEN} value={status} onChange={setStatus} />
        </div>
        {status === 'draft' ? (
          <OpnieuwAlles venster={venster} model={model} platform={platform} totaal={q.data?.total ?? 0}
                        laden={q.loading} onKlaar={opnieuw} />
        ) : null}
        {status === 'draft' && q.data?.can_approve ? (
          <LijstActies soort="approve" venster={venster} model={model} platform={platform}
                       weekNaam={weekLabel(maandag)} laden={q.loading} versie={q.data?.total ?? -1} onKlaar={opnieuw} />
        ) : null}
        {status === 'approved' && kanWijzigen ? (
          <LijstActies soort="send" venster={venster} model={model} platform={platform}
                       weekNaam={weekLabel(maandag)} laden={q.loading} versie={q.data?.total ?? -1} onKlaar={opnieuw} />
        ) : null}
        {q.loading && !q.data ? <Loading /> : q.error ? <ErrorNote error={q.error} /> : !groepen.length ? (
          <>
            <Empty>No automatic drafts for this week and filter.</Empty>
            {leeg && status === 'draft' && eldersMaandag && eldersMaandag !== maandag ? (
              <div className="row" data-testid="mm-auto-elders" style={{ gap: 10, alignItems: 'center', justifyContent: 'center' }}>
                <span className="mut2">{eldersQ.data?.total ?? 0} draft(s) to review in other weeks · first: {weekLabel(eldersMaandag)}</span>
                <button className="btn sm" type="button" onClick={() => setMaandag(eldersMaandag)}>Show that week</button>
              </div>
            ) : null}
          </>
        ) : (
          <div className="col" style={{ gap: 12 }}>
            {groepen.map((g) => {
              const klaar = goedTeKeuren(g, gezien)
              const open = g.items.filter((c) => c.status === 'draft')
              return (
                <div key={g.sleutel} className="card" data-testid="mm-auto-group">
                  <div className="row" style={{ justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                    <b>{g.model_name} · {dagNaam(g.dag)}</b>
                    <span className="mut2" data-testid="mm-auto-seen">
                      Seen {open.filter((c) => isGezien(gezien, c)).length} of {open.length}
                    </span>
                    {status === 'draft' && q.data?.can_approve ? (
                      <button className="btn primary sm" type="button" data-testid="mm-auto-approve"
                              disabled={busy !== null || klaar.length === 0}
                              title={klaar.length === 0 ? 'Scroll through the texts first: only texts you have seen can be approved.' : undefined}
                              onClick={() => run({
                                label: 'Approve',
                                confirm: `Approve ${klaar.length} automatic Mass Message${klaar.length === 1 ? '' : 's'} for ${g.model_name} on ${dagNaam(g.dag)}? `
                                  + 'Nothing is scheduled yet: approved messages only go out after an owner presses Send approved.',
                                request: () => api.post(`${CONCEPTEN}/approve-batch`, bulkVerzoek(g, gezien)),
                                successText: `${klaar.length} approved.`,
                                onDone: opnieuw,
                              })}>
                        Approve these {klaar.length}
                      </button>
                    ) : null}
                  </div>
                  <div className="col" style={{ gap: 8, marginTop: 8 }}>
                    {g.items.map((c) => (
                      <GezienWaarnemer key={c.id} onGezien={() => setGezien((m) => markeerGezien(m, c))}>
                        <ConceptRegel c={c} gezien={isGezien(gezien, c)} zone={zone} onKlaar={opnieuw} />
                      </GezienWaarnemer>
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
        )}
        <div className="row" style={{ gap: 8, marginTop: 10, alignItems: 'center' }}>
          <button className="btn ghost sm" type="button" disabled={offset === 0}
                  onClick={() => setOffset(Math.max(0, offset - PER_PAGINA))}>‹ Previous page</button>
          <span className="mut2">{q.data ? `${q.data.total} drafts` : ''}</span>
          <button className="btn ghost sm" type="button" disabled={q.data?.next_offset == null}
                  onClick={() => setOffset(q.data?.next_offset ?? offset)}>Next page ›</button>
        </div>
      </Panel>
    </div>
  )
}

function dagNaam(dag: string): string {
  // 16-10-2026 (Jordy): DD-MM-YYYY ("Thu 22-10-2026").
  return nlDagLabel(dag)
}

/** Meldt één keer "gezien" als minstens 60% van het blok in beeld is geweest. */
function GezienWaarnemer({ onGezien, children }: { onGezien: () => void; children: ReactNode }) {
  const ref = useRef<HTMLDivElement | null>(null)
  const melden = useRef(onGezien)
  melden.current = onGezien
  useEffect(() => {
    const el = ref.current
    if (!el || typeof IntersectionObserver === 'undefined') return
    const io = new IntersectionObserver((ingangen) => {
      if (ingangen.some((i) => i.isIntersecting && i.intersectionRatio >= 0.6)) melden.current()
    }, { threshold: [0.6] })
    io.observe(el)
    return () => io.disconnect()
  }, [children])
  return <div ref={ref}>{children}</div>
}

function ConceptRegel({ c, gezien, zone, onKlaar }: { c: AutoConcept; gezien: boolean; zone: string; onKlaar: () => void }) {
  const { run, busy } = useActions()
  const [open, setOpen] = useState(false)
  const mij = useMij()
  const personas = useApi<Persona[]>(open ? '/api/admin/models/personas' : null)
  const keuzes = useMemo(() => keuzesVoorConcept(modelKeuzes(personas.data ?? []), c), [personas.data, c])
  const tijd = lokaleTijd(c)
  return (
    <div className="card" data-testid="mm-auto-item" data-seen={gezien ? '1' : '0'}
         style={{ borderLeft: `3px solid ${gezien ? 'var(--ok, #2e7d32)' : 'var(--line, #ccc)'}` }}>
      <div className="row" style={{ gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        <b className="mono">{tijd || '—'}</b>
        <span className="mut2">{c.auto?.timezone || zone}</span>
        <Plat value={c.platform} />
        {c.auto?.level ? <Tag kind="acc">{NIVEAU_NAAM[c.auto.level] ?? c.auto.level}</Tag> : null}
        {c.auto?.daypart ? <Tag kind="mut">{c.auto.daypart}</Tag> : null}
        <Tag kind={c.is_paid ? 'warn' : 'mut'}>{typeLabel(c)}</Tag>
        {c.only_test ? <Tag kind="warn">test accounts only</Tag> : null}
        {c.auto?.regenerated ? <Tag kind="mut">regenerated</Tag> : null}
      </div>
      <div style={{ whiteSpace: 'pre-wrap', marginTop: 6 }} data-testid="mm-auto-text">{c.content}</div>
      {c.problems?.length ? (
        <ul className="massa-slot-errors" role="alert">{c.problems.map((p) => <li key={p}>{p}</li>)}</ul>
      ) : null}
      <div className="row" style={{ gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
        {c.status === 'draft' ? (
          // 11-10-2026 (Jordy): het volledige Mass Message-menu, om er handmatig een PPV van te maken.
          <button className="btn ghost sm" type="button" data-testid="mm-auto-edit" onClick={() => setOpen(true)}>
            Edit / make PPV
          </button>
        ) : null}
        {c.status === 'draft' ? (
          <button className="btn ghost sm" type="button" disabled={busy !== null}
                  onClick={() => run({
                    label: 'Reject',
                    confirm: 'Reject this text? It is never sent. You can regenerate it later.',
                    request: () => api.post(`${CONCEPTEN}/${c.id}/reject`),
                    successText: 'Rejected.',
                    onDone: onKlaar,
                  })}>Reject</button>
        ) : null}
        {c.status === 'draft' || c.status === 'draft_rejected' ? (
          <button className="btn ghost sm" type="button" disabled={busy !== null}
                  onClick={() => run({
                    label: 'Regenerate',
                    request: () => api.post(`${CONCEPTEN}/${c.id}/regenerate`),
                    successText: 'New text ready. Read it before approving.',
                    onDone: onKlaar,
                  })}>Regenerate</button>
        ) : null}
      </div>
      {open && personas.data ? (
        <ConceptBewerken c={c as unknown as Concept} keuzes={keuzes} zone={zone} auto
                         magDirect={magMassDm(mij.data, 'direct')} magNative={magMassDm(mij.data, 'native')}
                         magBetaald={magBetaaldeMassDm(mij.data)}
                         onSluit={() => setOpen(false)}
                         onOpgeslagen={() => { setOpen(false); onKlaar() }} />
      ) : null}
    </div>
  )
}

function Schakelaars({ inst, laden, fout, kanWijzigen, onKlaar, onToon, zone }: {
  inst: Instellingen | null; laden: boolean; fout: unknown; kanWijzigen: boolean; onKlaar: () => void
  onToon: (iso: string | null) => void
  zone: string
}) {
  const { run, busy } = useActions()
  // 16-10-2026 (Jordy): Generate (week en all) vanaf een gekozen startmoment, standaard nu.
  const [genMoment, setGenMoment] = useState<DatumTijd>(() => standaardMoment(Date.now(), zone))
  const genIso = zoneNaarUtc(genMoment.datum, genMoment.tijd, zone)
  const [genFout, setGenFout] = useState('')
  const genereerModel = async (m: ModelStand) => {
    setGenFout('')
    if (!genIso) { setGenFout('Pick a valid start date and time.'); return }
    let p: GenPreview
    try {
      p = await api.post<GenPreview>(`${AUTO}/generate`, { model_slug: m.slug, start: genIso, dry_run: true })
    } catch (e) {
      setGenFout(e instanceof Error ? e.message : String(e))
      return
    }
    await run({
      label: 'Generate week',
      confirm: `Generate 7 days of drafts for ${m.name} from ${alsZone(genIso, zone)} (${zoneLabel(zone)})? `
        + `${p.slots.length} planned; ${p.moved_to_after_start} moved to after the start; `
        + `${p.dropped_before_start} left out (before the start, no room that day)`
        + `${p.skipped_days.length ? `; ${p.skipped_days.length} day(s) skipped (already have drafts)` : ''}. Nothing is sent.`,
      request: () => api.post(`${AUTO}/generate`, { model_slug: m.slug, start: genIso }),
      successText: 'Drafts generated. Review them below.',
      onDone: () => { onToon(genIso); onKlaar() },
    })
  }
  const [caps, setCaps] = useState<{ d: number; u: number; w: number } | null>(null)
  useEffect(() => {
    if (inst) setCaps({ d: inst.daily_cap_per_model, u: inst.hourly_cap_per_brand, w: inst.generation_weekday })
  }, [inst])
  if (laden && !inst) return <Loading />
  if (fout) return <ErrorNote error={fout} />
  if (!inst) return null
  const zet = (slug: string, veld: 'generate' | 'send' | 'night_slots', waarde: boolean) => run({
    label: 'Switch',
    confirm: veld === 'send' && waarde
      ? 'Switch on automatic sending for this model? Approved automatic Mass Messages will then go out at their time.'
      : undefined,
    request: () => api.patch(`${AUTO}/models/${encodeURIComponent(slug)}`, { [veld]: waarde }),
    successText: 'Saved.',
    onDone: onKlaar,
  })
  // 12-10-2026 (Jordy): alle modellen in één klik aan/uit voor Generate of Send. Opslaan
  // zoals bij de losse vinkjes: per model dezelfde PATCH (met zijn eigen auditregel).
  const zetAlle = (veld: 'generate' | 'send', waarde: boolean) => {
    const anders = inst.models.filter((m) => m[veld] !== waarde)
    const naam = veld === 'generate' ? 'Generate' : 'Send'
    return run({
      label: `${naam} for all models`,
      confirm: veld === 'send' && waarde
        ? `Switch on automatic SENDING for all models (${anders.length} to change)? Every approved automatic Mass `
          + 'Message of these models will then go out to fans at its time.'
        : `Switch ${waarde ? 'on' : 'off'} ${naam} for all models (${anders.length} to change)?`,
      request: async () => {
        for (const m of anders) {
          await api.patch(`${AUTO}/models/${encodeURIComponent(m.slug)}`, { [veld]: waarde })
        }
      },
      successText: 'Saved.',
      onDone: onKlaar,
    })
  }
  return (
    <Panel title="Automatic Mass Messages" desc={kanWijzigen ? 'Only an owner can change these.' : 'Read only: only an owner can change these.'}>
      {/* 12-10-2026 (Jordy): boven de tabel, zodat de knop niet onder de lijst wegvalt. */}
      {kanWijzigen ? (
        <div className="row" style={{ gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 6 }}
             data-testid="mm-auto-generate-start">
          <TijdKiezer datum={genMoment.datum} tijd={genMoment.tijd} onChange={setGenMoment} zone={zone} naam="Generate from"
                      label={`Generate from (${zoneLabel(zone)})`}
                      hint="Generate (all models or one) makes 7 days of drafts starting at this moment." />
          {genFout ? <span className="mut2">{genFout}</span> : null}
        </div>
      ) : null}
      <GenereerAlles inst={inst} kanWijzigen={kanWijzigen} onKlaar={onKlaar} onToon={onToon}
                     startIso={genIso} zone={zone} />
      {!inst.system_account.exists ? (
        <div className="note" data-testid="mm-auto-no-account">The system account for automatic Mass Messages has not been set up yet.</div>
      ) : !inst.system_account.active ? (
        <div className="note" data-testid="mm-auto-stopped">The system account is inactive: no automatic Mass Message is sent (emergency stop).</div>
      ) : null}
      {inst.drafts_expired_last_7_days > 0 ? (
        <div className="note">{inst.drafts_expired_last_7_days} automatic draft(s) expired in the last 7 days without approval.</div>
      ) : null}
      <div className="tblwrap">
      <table className="tbl" data-testid="mm-auto-switches">
        <thead><tr><th>Model</th><th>Platform</th><th>Time zone</th><th>
          <AllesVinkje modellen={inst.models} veld="generate" disabled={!kanWijzigen || busy !== null}
                       onZet={(w) => { void zetAlle('generate', w) }} /> Generate
        </th><th>
          <AllesVinkje modellen={inst.models} veld="send" disabled={!kanWijzigen || busy !== null}
                       onZet={(w) => { void zetAlle('send', w) }} /> Send
        </th><th>Night slots</th><th /></tr></thead>
        <tbody>
          {inst.models.map((m) => (
            <tr key={m.slug}>
              <td>{m.name}</td>
              <td><Plat value={m.platform} /></td>
              <td className="mut2">{m.timezone}</td>
              {(['generate', 'send', 'night_slots'] as const).map((veld) => (
                <td key={veld}>
                  <input type="checkbox" checked={m[veld]} disabled={!kanWijzigen || busy !== null}
                         aria-label={`${veld} ${m.name}`} onChange={(e) => { void zet(m.slug, veld, e.target.checked) }} />
                </td>
              ))}
              <td>
                {kanWijzigen ? (
                  <button className="btn ghost sm" type="button" disabled={!m.generate || busy !== null || !genIso}
                          title={m.generate ? 'Generate 7 days from the "Generate from" moment above' : 'Switch on Generate first.'}
                          onClick={() => { void genereerModel(m) }}>Generate 7 days</button>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      {caps ? (
        <div className="row" style={{ gap: 10, marginTop: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <label>Per model per day <input type="number" min={1} max={10} value={caps.d} disabled={!kanWijzigen}
                 onChange={(e) => setCaps({ ...caps, d: Number(e.target.value) })} style={{ width: 60 }} /></label>
          <label>Per brand per hour <input type="number" min={1} max={60} value={caps.u} disabled={!kanWijzigen}
                 onChange={(e) => setCaps({ ...caps, u: Number(e.target.value) })} style={{ width: 60 }} /></label>
          <label>Generate on <select value={caps.w} disabled={!kanWijzigen}
                 onChange={(e) => setCaps({ ...caps, w: Number(e.target.value) })}>
            {WEEKDAG_NAAM.map((n, i) => <option key={n} value={i}>{n}</option>)}
          </select></label>
          {kanWijzigen ? (
            <button className="btn sm" type="button" disabled={busy !== null}
                    onClick={() => run({
                      label: 'Save limits',
                      request: () => api.patch(`${AUTO}/caps`, {
                        daily_cap_per_model: caps.d, hourly_cap_per_brand: caps.u, generation_weekday: caps.w }),
                      successText: 'Saved.',
                      onDone: onKlaar,
                    })}>Save limits</button>
          ) : null}
          {inst.last_generated_week ? <span className="mut2">Last weekly run: week of {inst.last_generated_week}</span> : null}
          <span className="mut2" style={{ fontSize: 12 }} data-testid="mm-auto-caps-uitleg">
            These limits apply to Generate. An owner or admin can still schedule or move a message into a full hour by hand.
          </span>
        </div>
      ) : null}
    </Panel>
  )
}

/** Kopvinkje: aan als alle modellen aan staan, half als sommige; een klik zet alles aan (of uit als alles al aan stond). */
function AllesVinkje({ modellen, veld, disabled, onZet }: {
  modellen: ModelStand[]; veld: 'generate' | 'send'; disabled: boolean; onZet: (waarde: boolean) => void
}) {
  const ref = useRef<HTMLInputElement>(null)
  const aan = modellen.filter((m) => m[veld]).length
  const alles = modellen.length > 0 && aan === modellen.length
  const deels = aan > 0 && !alles
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = deels
  }, [deels])
  return (
    <input ref={ref} type="checkbox" checked={alles} disabled={disabled || !modellen.length}
           aria-label={`${veld} all models`} data-testid={`mm-auto-all-${veld}`}
           aria-checked={deels ? 'mixed' : alles}
           onChange={() => onZet(!alles)} style={{ verticalAlign: 'middle', marginRight: 4 }} />
  )
}

interface LijstUitkomst {
  approved?: number
  scheduled?: number
  skipped: number
  skipped_reasons: Record<string, number>
  test_excluded: number
}

/**
 * 12-10-2026 (Jordy): "Approve all" (alle concepten in de lijst To review, nooit Test
 * accounts only; er wordt niets ingepland) en "Send approved" (owner: plant de
 * goedgekeurde berichten in; pas dan gaan ze op hun tijd uit, als Send voor het
 * model aan staat). Eerst telt de server (dry run), dan de bevestiging met dat aantal.
 */
/** Het filter voor "alles": elk open bericht vanaf nu, alle weken, modellen en platformen. */
function allesFilter() {
  const nu = Date.now()
  return { from: new Date(nu).toISOString(), to: new Date(nu + 400 * 86_400_000).toISOString(),
           model_slug: null, platform: null }
}

/**
 * 16-10-2026 (Jordy): "alle drafts in één keer kunnen approven", en de knop moet
 * zeggen wat hij doet. Twee knoppen, elk met de telling van de server erin:
 * "Approve all N" (alle weken, modellen, platformen) en, als die lijst anders is,
 * "Approve this list (N)" (deze week + filter). Hetzelfde voor Send approved.
 */
function LijstActies({ soort, venster, model, platform, weekNaam, laden, versie, onKlaar }: {
  soort: 'approve' | 'send'; venster: { from: string; to: string }; model: string; platform: string
  weekNaam: string; laden: boolean; versie: number; onKlaar: () => void
}) {
  const pad = soort === 'approve' ? `${AUTO}/approve-all` : `${AUTO}/send-approved`
  const [tel, setTel] = useState<{ alles: number | null; lijst: number | null }>({ alles: null, lijst: null })
  const lijstFilter = { from: venster.from, to: venster.to, model_slug: model || null, platform: platform || null }
  useEffect(() => {
    let levend = true
    const telling = (f: object) => api.post<{ count: number }>(pad, { ...f, dry_run: true })
      .then((r) => r.count).catch(() => null)
    void Promise.all([telling(allesFilter()), telling(lijstFilter)]).then(([alles, lijst]) => {
      if (levend) setTel({ alles, lijst })
    })
    return () => { levend = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pad, venster.from, venster.to, model, platform, versie])
  const filterNaam = [weekNaam, model || null, platform === 'f2f' ? 'F2F' : platform === 'fancentro' ? 'FC' : null]
    .filter(Boolean).join(' · ')
  return (
    <div className="col" style={{ gap: 4 }}>
      <LijstActie soort={soort} scope="all" filter={allesFilter} aantal={tel.alles} laden={laden}
                  uitleg="every week, every model, FC and F2F" onKlaar={onKlaar} />
      {tel.lijst != null && tel.lijst !== tel.alles ? (
        <LijstActie soort={soort} scope="list" filter={() => lijstFilter} aantal={tel.lijst} laden={laden}
                    uitleg={`only this list: ${filterNaam}`} onKlaar={onKlaar} />
      ) : null}
    </div>
  )
}

function LijstActie({ soort, scope, filter: maakFilter, aantal, uitleg, laden, onKlaar }: {
  soort: 'approve' | 'send'; scope: 'all' | 'list'; filter: () => object; aantal: number | null
  uitleg: string; laden: boolean; onKlaar: () => void
}) {
  const { run, busy } = useActions()
  const [melding, setMelding] = useState('')
  const pad = soort === 'approve' ? `${AUTO}/approve-all` : `${AUTO}/send-approved`
  const telTekst = aantal == null ? '…' : String(aantal)
  const naam = soort === 'approve'
    ? (scope === 'all' ? `Approve all ${telTekst}` : `Approve this list (${telTekst})`)
    : (scope === 'all' ? `Send all ${telTekst} approved` : `Send this list (${telTekst})`)
  const start = async () => {
    setMelding('')
    const filter = maakFilter()
    let telling: { count: number; test_excluded: number }
    try {
      telling = await api.post<{ count: number; test_excluded: number }>(pad, { ...filter, dry_run: true })
    } catch (e) {
      setMelding(e instanceof Error ? e.message : String(e))
      return
    }
    if (!telling.count) {
      setMelding(soort === 'approve' ? 'There is nothing to approve in this list.' : 'There is nothing approved to send in this list.')
      return
    }
    const test = telling.test_excluded ? ` ${telling.test_excluded} Test accounts only draft(s) are left out.` : ''
    await run({
      label: naam,
      confirm: soort === 'approve'
        ? `Approve ${telling.count} draft(s) — ${uitleg}?${test} Nothing is scheduled `
          + 'or sent: approved messages only go out after an owner presses Send approved.'
        : `Schedule ${telling.count} approved message(s) — ${uitleg}?${test} They will go `
          + 'out to fans at their time, for every model where Send is switched on.',
      request: async () => {
        const r = await api.post<LijstUitkomst>(pad, { ...filter, expected_count: telling.count })
        const gedaan = soort === 'approve' ? r.approved : r.scheduled
        const redenen = Object.entries(r.skipped_reasons || {}).map(([t, n]) => `${n}× ${t}`).join('; ')
        setMelding(`${soort === 'approve' ? 'Approved' : 'Scheduled'}: ${gedaan ?? 0}.`
          + (r.skipped ? ` Not ${soort === 'approve' ? 'approved' : 'scheduled'}: ${r.skipped} (${redenen}).` : ''))
        return r
      },
      successText: soort === 'approve' ? 'Approved. Nothing is scheduled yet.' : 'Scheduled.',
      onDone: onKlaar,
    })
  }
  return (
    <div className="row" style={{ gap: 10, margin: '4px 0', flexWrap: 'wrap', alignItems: 'center' }}
         data-testid={`mm-auto-${soort}-${scope}`}>
      <button className={scope === 'all' ? 'btn primary sm' : 'btn ghost sm'} type="button"
              disabled={laden || busy !== null || aantal === 0} onClick={() => { void start() }}>{naam}</button>
      <span className="mut2" style={{ fontSize: 12 }}>{uitleg}</span>
      {melding ? <span className="mut2">{melding}</span> : null}
    </div>
  )
}

interface OpnieuwStand {
  running: boolean
  interrupted: boolean
  started_by?: string | null
  total: number
  done: number
  ok: number
  failed: number
  failures: { id: number; reason: string }[]
  stopped: string | null
}

/**
 * 12-10-2026 (Jordy): "Regenerate all": elk concept in de huidige lijst "To review"
 * (deze week + model/platform-filter) een nieuwe tekst, op de achtergrond. Per
 * concept dezelfde route als de losse Regenerate (generator, filters, scope).
 */
function OpnieuwAlles({ venster, model, platform, totaal, laden, onKlaar }: {
  venster: { from: string; to: string }; model: string; platform: string; totaal: number; laden: boolean
  onKlaar: () => void
}) {
  const { run, busy } = useActions()
  const stand = useApi<OpnieuwStand>(`${AUTO}/regenerate-all`)
  const loopt = Boolean(stand.data?.running)
  usePoll(4000, stand.refetch, loopt)
  const liep = useRef(false)
  const [vorige, setVorige] = useState(0)
  useEffect(() => {
    if (liep.current && !loopt) onKlaar()
    // Tussendoor ook de lijst verversen, zodat de nieuwe teksten verschijnen.
    if (loopt && stand.data && stand.data.done - vorige >= 10) { setVorige(stand.data.done); onKlaar() }
    liep.current = loopt
  }, [loopt, onKlaar, stand.data, vorige])
  const s = stand.data
  return (
    <div className="row" style={{ gap: 10, margin: '8px 0', flexWrap: 'wrap', alignItems: 'center' }}
         data-testid="mm-auto-regenerate-all">
      <button className="btn sm" type="button" disabled={!totaal || laden || loopt || busy !== null}
              onClick={() => run({
                label: 'Regenerate all',
                confirm: `Regenerate all ${totaal} draft(s) in this list (this week and filter)? Each draft gets a new `
                  + 'text from the same generator and checks; texts you already read must be read again. Nothing is '
                  + 'sent. Uses one AI request per draft and stops at the daily AI limit. This runs in the background.',
                request: async () => {
                  setVorige(0)
                  return api.post(`${AUTO}/regenerate-all`, {
                    from: venster.from, to: venster.to, model_slug: model || null, platform: platform || null,
                    expected_total: totaal,
                  })
                },
                successText: 'Regenerate all started.',
                onDone: stand.refetch,
              })}>Regenerate all ({totaal})</button>
      {s && s.total ? (
        loopt ? (
          <span className="mut2">Regenerating: {s.done} of {s.total} done ({s.failed} failed)…</span>
        ) : (
          <span className="mut2">
            {s.interrupted ? 'Last Regenerate all stopped early' : 'Last Regenerate all'}
            {s.started_by ? ` (by ${s.started_by})` : ''}: {s.ok} of {s.total} regenerated
            {s.failed ? `, ${s.failed} failed` : ''}.{s.stopped ? ` ${s.stopped}` : ''}
            {s.failures.length ? ` Not regenerated: ${s.failures.slice(0, 5).map((f) => `#${f.id} (${f.reason})`).join(', ')}`
              + (s.failures.length > 5 ? ' …' : '') : ''}
          </span>
        )
      ) : null}
    </div>
  )
}

interface GenPreview {
  slots: unknown[]; moved_to_after_start: number; dropped_before_start: number; skipped_days: string[]
}
interface AllesPreview {
  planned: number; moved_to_after_start: number; dropped_before_start: number
  models: { slug: string; name: string; planned: number; moved_to_after_start: number; dropped_before_start: number; error: string | null }[]
}

interface AllesModel {
  slug: string
  name?: string
  status: 'waiting' | 'busy' | 'done' | 'error'
  drafts?: number
  empty?: number
  skipped_days?: number
  error?: string
}

interface AllesStand {
  running: boolean
  interrupted: boolean
  week_start: string | null
  start?: string | null
  started_by?: string | null
  finished_at?: string | null
  models: AllesModel[]
}

/**
 * 12-10-2026 (Jordy): "Generate all": "Generate week" voor elk model met
 * Generate aan, in één keer en met exact dezelfde regels (de server doet per
 * model hetzelfde pad). Loopt op de achtergrond; hier de voortgang.
 */
function GenereerAlles({ inst, kanWijzigen, onKlaar, onToon, startIso, zone }: {
  inst: Instellingen; kanWijzigen: boolean; onKlaar: () => void; onToon: (iso: string | null) => void
  startIso: string | null; zone: string
}) {
  const { run, busy } = useActions()
  const stand = useApi<AllesStand>(`${AUTO}/generate-all`)
  const loopt = Boolean(stand.data?.running)
  usePoll(4000, stand.refetch, loopt)
  const liep = useRef(false)
  useEffect(() => {
    // Klaar: de nieuwe concepten meteen tonen.
    if (liep.current && !loopt) onKlaar()
    liep.current = loopt
  }, [loopt, onKlaar])
  const aan = inst.models.filter((m) => m.generate)
  const s = stand.data
  const klaar = s?.models.filter((m) => m.status === 'done' || m.status === 'error').length ?? 0
  // 16-10-2026 (TT-melding Jordy): de lijst ververst na ELK klaar model, niet pas
  // aan het eind; anders lijkt het halverwege of modellen overgeslagen worden.
  const vorigKlaar = useRef(klaar)
  useEffect(() => {
    if (loopt && klaar > vorigKlaar.current) onKlaar()
    vorigKlaar.current = klaar
  }, [klaar, loopt, onKlaar])
  const [preview, setPreview] = useState<AllesPreview | null>(null)
  const [fout, setFout] = useState('')
  useEffect(() => { setPreview(null); setFout('') }, [startIso])
  const start = async () => {
    if (!startIso) return
    setFout('')
    let p: AllesPreview
    try {
      p = await api.post<AllesPreview>(`${AUTO}/generate-all/preview`, { start: startIso })
    } catch (e) {
      setPreview(null)
      setFout(e instanceof Error ? e.message : String(e))
      return
    }
    setPreview(p)
    await run({
      label: 'Generate all',
      confirm: `Generate 7 days of drafts for all ${aan.length} model(s) with Generate on, from ${alsZone(startIso, zone)} `
        + `(${zoneLabel(zone)})? About ${p.planned} planned; ${p.moved_to_after_start} moved to after the start; `
        + `${p.dropped_before_start} left out (before the start, no room that day). Nothing is sent; days that already `
        + 'have drafts are skipped. Runs in the background, model by model.',
      request: () => api.post(`${AUTO}/generate-all`, { start: startIso }),
      successText: 'Generate all started.',
      onDone: () => { onToon(startIso); stand.refetch() },
    })
  }
  const concepten = s?.models.reduce((n, m) => n + (m.drafts ?? 0), 0) ?? 0
  const errs = s?.models.filter((m) => m.status === 'error') ?? []
  return (
    <div className="row" style={{ gap: 10, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}
         data-testid="mm-auto-generate-all">
      {kanWijzigen ? (
        <button className="btn sm" type="button" disabled={!aan.length || loopt || busy !== null || !startIso}
                title={aan.length ? undefined : 'Switch on Generate for at least one model first.'}
                onClick={() => { void start() }}>Generate all ({aan.length}) from {alsZone(startIso, zone)}</button>
      ) : null}
      {fout ? <span className="mut2">{fout}</span> : null}
      {preview && !loopt ? (
        <span className="mut2" style={{ fontSize: 12 }} data-testid="mm-auto-generate-preview">
          Plan: {preview.models.map((m) => `${m.name} ${m.error ? `✗ ${m.error}` : m.planned}`).join(' · ')}
        </span>
      ) : null}
      {s && s.week_start ? (
        loopt ? (
          <span className="mut2" data-testid="mm-auto-generate-progress">
            Generating from {s.start ? alsZone(s.start, zone) : nlDatum(s.week_start)}: {klaar} of {s.models.length} model(s) done —{' '}
            {s.models.map((m) => `${m.name ?? m.slug} ${m.status === 'done' ? `✓ ${m.drafts ?? 0}` : m.status === 'busy' ? '…' : m.status === 'error' ? '✗' : '·'}`).join(' · ')}
          </span>
        ) : (
          <span className="mut2">
            {s.interrupted ? 'Last Generate all stopped early' : 'Last Generate all'} (from {s.start ? alsZone(s.start, zone) : nlDatum(s.week_start)}
            {s.started_by ? `, by ${s.started_by}` : ''}): {concepten} draft(s) for {s.models.length} model(s)
            {errs.length ? `; not generated: ${errs.map((m) => `${m.name ?? m.slug} (${m.error ?? 'error'})`).join(', ')}` : ''}.
          </span>
        )
      ) : null}
    </div>
  )
}


interface StartPlan {
  count: number; offset_days: number; kept: number; moved: number; dropped: number
  approved: number; dropped_approved?: number; not_moved_scheduled: number
  /** 16-10-2026: in een ander dagdeel (of botsend tijdswoord): nieuwe tekst, terug naar To review. */
  regenerate: number
  regenerate_items: { id: number; model_slug: string; new: string | null; regenerate: string | null }[]
  old_first: string | null; old_last: string | null; new_first: string | null; new_last: string | null
  moved_items: { id: number; model_slug: string; old: string; new: string | null }[]
  dropped_items: { id: number; model_slug: string; old: string }[]
}

/**
 * 16-10-2026 (Jordy): "de startdatum/-tijd van de batch kunnen wijzigen". Eerst
 * een preview van de server (niets gewijzigd), dan één bevestiging. Alleen owner.
 * Er wordt niets goedgekeurd, ingepland of verzonden.
 */
function StartVerschuiven({ zone, onKlaar }: { zone: string; onKlaar: () => void }) {
  const { run, busy } = useActions()
  const [moment, setMoment] = useState<DatumTijd>(() => standaardMoment(Date.now(), zone))
  const [plan, setPlan] = useState<{ voor: string; data: StartPlan } | null>(null)
  const [fout, setFout] = useState('')
  const iso = zoneNaarUtc(moment.datum, moment.tijd, zone)
  useEffect(() => { setPlan(null) }, [moment.datum, moment.tijd])
  const preview = async () => {
    setFout('')
    if (!iso) { setFout('Pick a valid date and time.'); return }
    try {
      setPlan({ voor: iso, data: await api.post<StartPlan>(`${AUTO}/shift-start`, { start: iso, dry_run: true }) })
    } catch (e) {
      setFout(e instanceof Error ? e.message : String(e))
    }
  }
  const verschuif = async () => {
    if (!plan) return
    const p = plan.data
    await run({
      label: 'Move start',
      confirm: `Move ${p.count} automatic draft(s) to start ${alsZone(plan.voor, zone)}? `
        + `${p.kept} keep their time (${Math.abs(p.offset_days)} day(s) ${p.offset_days < 0 ? 'earlier' : 'later'}), `
        + `${p.moved} move to later that evening, ${p.dropped} do not fit and are removed (never sent). `
        + (p.regenerate ? `${p.regenerate} land in another part of the day: they get a NEW text (in the background) `
          + 'and go back to To review. ' : '')
        + 'Nothing is approved, scheduled or sent.',
      request: () => api.post<StartPlan>(`${AUTO}/shift-start`, { start: plan.voor, expected_count: p.count }),
      successText: 'Start moved. Nothing is scheduled yet: approve, then Send approved.',
      onDone: () => { setPlan(null); onKlaar() },
    })
  }
  const p = plan?.data
  return (
    <Panel title="Start of the automatic batch"
           desc={<>Move all upcoming automatic drafts (To review and Approved) so the batch starts at this moment. Whole days, same local time per model; content and status stay. Times in {zoneLabel(zone)}.</>}>
      <div className="row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }} data-testid="mm-auto-start">
        <TijdKiezer datum={moment.datum} tijd={moment.tijd} onChange={setMoment} zone={zone} naam="Batch start"
                    label={`New start (${zoneLabel(zone)})`} hint="The first day of the batch becomes this day." />
        <button className="btn sm" type="button" disabled={busy !== null} onClick={() => { void preview() }}>Preview</button>
        {p && p.count ? (
          <button className="btn primary sm" type="button" disabled={busy !== null} onClick={() => { void verschuif() }}>
            Move {p.count} drafts
          </button>
        ) : null}
      </div>
      {fout ? <div className="mut2" style={{ marginTop: 8 }}>{fout}</div> : null}
      {p ? (
        p.count === 0 ? <div className="mut2" style={{ marginTop: 8 }}>There are no upcoming automatic drafts to move.</div> : (
          <div className="mut2" style={{ marginTop: 10, fontSize: 13, lineHeight: 1.6 }} data-testid="mm-auto-start-plan">
            <div><b>{p.count}</b> drafts ({p.approved} approved): now {alsZone(p.old_first, zone)} – {alsZone(p.old_last, zone)} →
              {' '}<b>{alsZone(p.new_first, zone)} – {alsZone(p.new_last, zone)}</b></div>
            <div>
              <Tag kind="ok">{p.kept} keep their time</Tag>{' '}
              <Tag kind="warn">{p.moved} move to after the start</Tag>{' '}
              <Tag kind={p.dropped ? 'bad' : 'mut'}>{p.dropped} do not fit: removed, never sent</Tag>
              {p.dropped_approved ? <> <Tag kind="bad">{p.dropped_approved} of those were approved</Tag></> : null}
              {p.not_moved_scheduled ? <> <Tag kind="mut">{p.not_moved_scheduled} already scheduled: not moved</Tag></> : null}
              {p.regenerate ? <> <Tag kind="warn" title="Their text was written for another part of the day">{p.regenerate} get a new text (other part of the day)</Tag></> : null}
            </div>
            {p.regenerate_items?.length ? (
              <div style={{ fontSize: 12 }} data-testid="mm-auto-start-regenerate">New text: {p.regenerate_items.slice(0, 5).map((x) =>
                `${x.model_slug} ${alsZone(x.new, zone)} (${x.regenerate})`).join(' · ')}{p.regenerate > 5 ? ' …' : ''}</div>
            ) : null}
            {p.moved_items.length ? (
              <div style={{ fontSize: 12 }}>Moved: {p.moved_items.slice(0, 6).map((x) =>
                `${x.model_slug} ${alsZone(x.old, zone)} → ${alsZone(x.new, zone)}`).join(' · ')}{p.moved > 6 ? ' …' : ''}</div>
            ) : null}
            <div style={{ fontSize: 12 }}>Caps stay: max per model per day and per brand per hour. To go live: approve, then Send approved.</div>
          </div>
        )
      ) : null}
    </Panel>
  )
}
